import { Alert, Badge, Button, Group, Paper, Stack, Table, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { formatDateTime, formatMoney, formatQty } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { COUNTS_PATH, LEDGER_PATH, STOCK_PATH, useStockItems } from './stock';

interface WaitingLine {
  id: string;
  inventoryItemId: string;
  expectedQty: number | null;
  countedQty: number;
  unit: string;
  varianceQty: number | null;
  varianceCostBaseMinor: number | null;
  enteredQty: number | null;
  enteredUnit: string | null;
}

interface WaitingCount {
  id: string;
  storageLocationId: string;
  frozenAt: string | null;
  countedByName: string | null;
  lines: WaitingLine[];
}

/**
 * The counts somebody took and nobody accepted.
 *
 * Until this existed, walking away from a count sheet made that shelf permanently uncountable. A
 * second sheet on a shelf that already has an unapproved one is refused — it has to be, or the same
 * shortfall comes off twice — and there was no screen anywhere that could show the sheet doing the
 * blocking, let alone approve or retire it. The count dialogue offered "Leave it unapproved" as a
 * button and the API answered every later count of that area with a refusal naming an item.
 *
 * NOTHING AT ALL WHEN NOTHING IS WAITING, which is almost every day. A permanent empty panel under
 * a permanent heading is furniture, and furniture stops being read — the answer to "is a count
 * stuck" is this block being on the screen or not.
 *
 * Approving is the same act it always was. Throwing one away takes a reason, because a count that
 * simply disappears leaves nobody able to say whether the shelf was ever really wrong.
 */
export function CountsWaiting({ currency }: { currency: string }) {
  const queryClient = useQueryClient();
  const areas = useEntityIndex('storageLocation');
  const items = useEntityIndex('inventoryItem');
  const stock = useStockItems();
  /** Which sheet is being thrown away, and the words being typed for why. */
  const [discarding, setDiscarding] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const waiting = useQuery({
    queryKey: listKey(COUNTS_PATH, { waiting: 'true' }),
    queryFn: () => rawApi<WaitingCount[]>('GET', `${COUNTS_PATH}?waiting=true`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(COUNTS_PATH) });
    void queryClient.invalidateQueries({ queryKey: listKey(STOCK_PATH) });
    void queryClient.invalidateQueries({ queryKey: listKey(LEDGER_PATH) });
  };

  const approve = useMutation({
    mutationFn: (id: string) => rawApi('POST', `${COUNTS_PATH}/${id}/approve`),
    onSuccess: () => {
      notifyDone('Count approved', 'The shelf figures now match what was counted.');
      refresh();
    },
    onError: (err: unknown) => notifyError(err, 'approve this count'),
  });

  const discard = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      rawApi('POST', `${COUNTS_PATH}/${input.id}/discard`, { reason: input.reason }),
    onSuccess: () => {
      notifyDone('Count thrown away', 'Nothing moved. That area can be counted again.');
      setDiscarding(null);
      setReason('');
      refresh();
    },
    onError: (err: unknown) => notifyError(err, 'throw this count away'),
  });

  const counts = waiting.data ?? [];
  if (waiting.isLoading || counts.length === 0) return null;

  return (
    <Stack gap="md" mb="xl">
      <Stack gap={4}>
        <Group gap="sm" align="baseline">
          <Text fw={600}>Counts waiting for you</Text>
          <Badge color="yellow" variant="light">
            {counts.length}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Nothing on these has changed the shelf yet, and the areas they cover cannot be counted again until
          each one is either approved or thrown away.
        </Text>
      </Stack>

      {counts.map((count) => (
        <Paper key={count.id} withBorder p="md" radius="sm">
          <Group justify="space-between" align="flex-start" mb="sm" wrap="wrap">
            <Stack gap={2}>
              <Text fw={500}>{areas.label(count.storageLocationId)}</Text>
              <Text size="xs" c="dimmed">
                {/* Counted at, not submitted at — and by whom. The name travels with the count
                    because the identifier beside it resolves to nobody outside the directory, and
                    on a sheet taken at the register it is not even the same kind of identifier. */}
                Counted {formatDateTime(count.frozenAt)}
                {count.countedByName ? ` by ${count.countedByName}` : ''}
              </Text>
            </Stack>
            <Group gap="xs">
              <Button
                size="compact-sm"
                variant="default"
                onClick={() => {
                  setDiscarding(discarding === count.id ? null : count.id);
                  setReason('');
                }}
              >
                Throw it away
              </Button>
              <Button
                size="compact-sm"
                loading={approve.isPending && approve.variables === count.id}
                onClick={() => approve.mutate(count.id)}
              >
                Approve the count
              </Button>
            </Group>
          </Group>

          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Item</Table.Th>
                <Table.Th>Expected</Table.Th>
                <Table.Th>Counted</Table.Th>
                <Table.Th>Difference</Table.Th>
                <Table.Th>Worth</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {count.lines.map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td>
                    {items.label(line.inventoryItemId) || stock.nameOf(line.inventoryItemId)}
                  </Table.Td>
                  <Table.Td>{formatQty(line.expectedQty ?? 0, line.unit)}</Table.Td>
                  <Table.Td>
                    {formatQty(line.countedQty, line.unit)}
                    {line.enteredUnit && (
                      <Text size="xs" c="dimmed">
                        written down as {formatQty(line.enteredQty ?? 0, line.enteredUnit)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td c={(line.varianceQty ?? 0) < 0 ? 'red.7' : undefined}>
                    {(line.varianceQty ?? 0) > 0 ? '+' : ''}
                    {formatQty(line.varianceQty ?? 0, line.unit)}
                  </Table.Td>
                  <Table.Td c={(line.varianceCostBaseMinor ?? 0) < 0 ? 'red.7' : undefined}>
                    {formatMoney(line.varianceCostBaseMinor ?? 0, currency)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {discarding === count.id && (
            <Alert color="yellow" variant="light" mt="sm" title="Why is this count not being used?">
              <Stack gap="xs">
                <Text size="sm">
                  Nothing will move and nothing is deleted — the sheet stays on file with your reason on it,
                  and the area can be counted again.
                </Text>
                <TextInput
                  placeholder="Counted the wrong area, miscounted, too old to trust…"
                  value={reason}
                  onChange={(e) => setReason(e.currentTarget.value)}
                />
                <Group justify="flex-end" gap="xs">
                  <Button size="compact-sm" variant="subtle" onClick={() => setDiscarding(null)}>
                    Keep it
                  </Button>
                  <Button
                    size="compact-sm"
                    color="red"
                    disabled={reason.trim().length === 0}
                    loading={discard.isPending}
                    onClick={() => discard.mutate({ id: count.id, reason: reason.trim() })}
                  >
                    Throw it away
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}
        </Paper>
      ))}
    </Stack>
  );
}
